/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import * as CommonIcons from 'assets/common';
import { Typography } from '..';
import {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  RegisterOptions,
  useController,
  Validate,
} from 'react-hook-form';
import { getDataPropsFromRest, isNullOrUndefined } from 'utilities/Utils';
import { StyledEngineProvider } from '@mui/material';

// Type defination for TextArea.tsx

type CommonProps = {
  name?: string;
  placeholder?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  className?: string;
  rows?: number;
  rowsMax?: number;
  hideHelpText?: boolean;
  autoFocus?: boolean;
};
type CharacterLimitProps = {
  characterLimit?: 'string' | 'number' | any;
};
type MultiLineInputProps = CharacterLimitProps &
CommonProps & {
  value: 'string' | 'number' | any;
  onChange?: (
    newVal: string | number | any,
    name?: string | number | any,
  ) => void;
};

type ControlledMultiLineInputProps<
  T extends FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> = CommonProps & {
  name: TName;
  control: Control<T, unknown>;
  defaultValue?: string | number;
  isRequired?: boolean;
  maxLength?: number;
  minLength?: number;
  customValidator?: Validate<PathValue<T, TName>, T>;
};

//end of type definition

const useStyles = makeStyles(theme => ({
  smallPadding: {
    '& > div': {
      padding: '10px 12px',
    },
    '& > div > textarea': {
      lineHeight: '22px',
    },
  },
  helperTextCharCount: {
    margin: '3px 0 0',
    textAlign: 'right',
  },
  helperTextError: {
    margin: '3px 0 0',
    textAlign: 'left',
  },
  errContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  errorIcon: {
    marginRight: theme.spacing(1),
  },
}));

function MultiLineInputComp(
  props: MultiLineInputProps,
  ref: React.Ref<HTMLElement>,
) {
  const {
    value,
    onChange,
    rows = 4,
    rowsMax,
    name,
    placeholder = '',
    fullWidth = true,
    disabled = false,
    errorMessage = '',
    className = '',
    characterLimit = 30,
    hideHelpText = false,
    autoFocus = false,
    ...rest
  } = props;

  const {
    helperTextError,
    errContainer,
    errorIcon,
    helperTextCharCount,
    smallPadding,
  } = useStyles({ value });

  const dataTestIdProps = getDataPropsFromRest(rest);

  const inputChangeHandler = evt => {
    const { name: _name, value: _value } = evt.target;
    return onChange(_value, _name);
  };

  const hasError = errorMessage !== '';
  let helperText = null;
  if (hasError) {
    helperText = (
      <div className={errContainer}>
        <CommonIcons.Error className={errorIcon} />
        <Typography variant="subtitle1" themeColor="error.main">
          {errorMessage}
        </Typography>
      </div>
    );
  } else if (!hideHelpText) {
    helperText = `${value.length} of  ${characterLimit}`;
  }

  return (
    <StyledEngineProvider injectFirst>
      <TextField
        autoFocus={autoFocus}
        value={value}
        onChange={inputChangeHandler}
        inputRef={ref}
        multiline={true}
        rows={rows}
        maxRows={rowsMax}
        name={name}
        fullWidth={fullWidth}
        disabled={disabled}
        error={hasError}
        classes={{ root: `${smallPadding} ${className}` }}
        placeholder={placeholder}
        inputProps={{ maxlength: characterLimit }}
        variant="outlined"
        helperText={helperText}
        FormHelperTextProps={{
          className: hasError
            ? helperTextError
            : `${helperTextCharCount} ${
              value.length === characterLimit ? 'text-warning-default' : ''
            }`,
        }}
        {...dataTestIdProps}
      />
    </StyledEngineProvider>
  );
}

const MultiLineInput = forwardRef(MultiLineInputComp);

const ControlledMultiLineInput = <
  T extends FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>(
    props: ControlledMultiLineInputProps<T, TName>,
  ) => {
  const {
    name,
    control,
    defaultValue = '',
    isRequired = false,
    maxLength,
    minLength,
    customValidator,
    ...rest
  } = props;

  const validations: Partial<RegisterOptions<T, TName>> = {
    required: {
      value: isRequired,
      message: 'This field is required',
    },
  };
  if (!isNullOrUndefined(maxLength)) {
    validations.maxLength = {
      value: maxLength,
      message: `The value cannot be longer than ${maxLength} characters`,
    };
  }
  if (!isNullOrUndefined(minLength)) {
    validations.minLength = {
      value: minLength,
      message: `The value cannot be shorter than ${minLength} characters`,
    };
  }
  if (typeof customValidator === 'function') {
    validations.validate = customValidator;
  }

  const {
    field: { ref, value, onChange },
  } = useController<T, TName>({
    name,
    control,
    defaultValue: defaultValue as PathValue<T, TName>,
    rules: validations,
  });

  return (
    <StyledEngineProvider injectFirst>
      <MultiLineInput
        ref={ref}
        value={value}
        onChange={onChange}
        characterLimit={maxLength ?? 30}
        {...rest}
      />
    </StyledEngineProvider>
  );
};

export { MultiLineInput, ControlledMultiLineInput };
